<template>
	<div class="customer-person-details-container">
		<AppBottomSheet :active="person !== null" :forceWidth="bottomSheetWidth" @toggling-popup="togglingPopup" >
			<div class="person-details-container" v-if="person !== null">
				<div class="sheet-heading">
					<div class="sheet-heading-first-row">
						<div class="row pointer flex-start align-center"
							 :title="getString('go_to_x', [
								 tabIndex === 1 ?
								 getString('history') :
								 getString('edit_customer')
							 ])"
							 @click="switchedTab"
						>
							<div class="edit-icon" :class="{active: tabIndex === 1}" v-html="svgStore.get('iconEdit')"></div>
							<p class="sheet-title">{{person?.name}}</p>
						</div>
						<div class="row flex-end first-row-ctas">
							<div class="print-icon-container pointer" @click.stop="printCustomerCreditQr">
								<div class="print-icon" v-html="svgStore.get('iconPrinter')"></div>
							</div>
							<AppButton :small="windowWidth < 600" outline :text="mainCtaText" @click.stop="tabButtonAction" />
						</div>
					</div>
					<div class="row centered credit-display-container">
						<div class="col-6 mobile-12 credit-display centered debit" @click="tabIndex = 0" :title="getString('go_to_x', [getString('history')])">
							<div class="row centered"><p class="balance-label">{{getString('debit_x', [currencyStore.currCurrency.symbol])}}</p></div>
							<div class="row centered">
								<p class="balance-value">
									-{{currencyStore.readableCurrencyValue(person?.debit, true, false)}}
								</p>
							</div>
						</div>
						<div :class="{animating: animatingCredit}" class="col-6 mobile-12 credit-display centered credit" @click="tabIndex = 2" :title="getString('go_to_x', [getString('topups')])">
							<div class="row centered"><p class="balance-label">{{getString('credit_x', [currencyStore.currCurrency.symbol])}}</p></div>
							<div class="row centered">
								<p class="balance-value">
									+{{customerCredit}}
								</p>
							</div>
						</div>
					</div>
				</div>
				<div class="tabs-container mt-10">
					<div class="orders-container" v-if="tabIndex === 2">
						<div class="tabs-container">
							<AppTable
								ref="topups-table"
								:fetch-function="fetchFunctionPersonCreditTopups"
								:table-headings="topupsTableHeadings"
								:filters="topupsFilters"
								:total-items="customersStore.personCreditTopupsTotal"
								:items="topupsToShow"
								initial-sort-field="date"
							>
								<template #custom-header>
									<h3>{{getString('topups')}}</h3>
								</template>
							</AppTable>
						</div>
					</div>
					<div class="orders-container" v-else-if="tabIndex === 0">
						<div class="tabs-container">
							<AppTable
								ref="people-table"
								:fetch-function="fetchFunctionPersonOrdersPayments"
								:table-headings="tableHeadings"
								:filters="filters"
								:total-items="customersStore.personOrdersTotal"
								:items="ordersToShow"
								initial-sort-field="date"
							>
								<template #settingsIcon="{item}">
									<div class="row centered">
										<AppTableButtonGroup v-if="item.type === 'paylater'" :buttons="buttonsPay(item)" :item="item" @picked-pay="addToCart(item)"/>
										<p v-else> - </p>
									</div>
								</template>
								<template #custom-header>
									<h3>{{getString('history')}}</h3>
									<div>
										<AppButton small outline :text="payButtonText" @click="openBottomPayment" :disabled="cartButtonDisabled" />
									</div>
								</template>
								<template #order_number="{item}">
									<div class="row centered">
										<p class="payment-intent" :class="{clickable: !!item.order_id}" @click="goToOrder(item.order_id)">{{ item.id }}</p>
									</div>
								</template>
							</AppTable>
						</div>
					</div>
					<div class="details-container mt-10" v-else-if="tabIndex === 1">
						<AppCard class="details-card" :title="getString('edit_customer')">
							<template v-slot:body>
								<CustomersPersonEditDetails @deleted-customer="emitDeletedPersonCustomer" ref="person-details" :person-id="personId" />
							</template>
						</AppCard>
					</div>
				</div>
			</div>
		</AppBottomSheet>
		<BottomNumberEditor
			:active="activeAddCreditNumberEditor"
			:title="getString('add_credit_x', [currencyStore.currCurrency.symbol])"
			:starting-number="person?.credit || 0"
			:start-price-name="getString('current_balance')"
			:discount-name="getString('adding_credit')"
			:total-name="getString('updated_balance')"
			no-actions
			show-all-shortcuts
			custom-shortcuts
			only-increment
			@toggling-popup="togglingCreditNumberEditor"
			@confirm-number="confirmCreditTopUp"
		/>
		<BottomPayment is-registry hide-customer-picker no-pay-later customer-not-editable :items-split="null" :active="paymentActive" @toggling-popup="togglingPayment" @payment-done="paymentDone" :total="counterStore.creditTopUpMode ? topUpCredit : cartTotal" :paid-value="0" @deleted-customer="emitDeletedPersonCustomer" :skip-tipping="true" />
	</div>
</template>

<script>
import AppBottomSheet from "@/shared/components/AppBottomSheet.vue";
import AppCard from "@/shared/components/AppCard.vue";
import WindowWidth from "@/console-new/mixins/WindowWidth";
import ModalHeader from "@/console-new/components/shared/ModalHeader.vue";
import {useCurrencyStore, useLanguageStore, useNotificationStore, useSvgStore, useValuesStore} from "@/shared/stores";
import {useCustomersStore, useEpsonStore, useOrdersStore, useCounterStore, useUserStore} from "@/console-new/stores";
import AppSelect from "@/shared/components/AppSelect.vue";
import AppGoogleMapsInput from "@/shared/components/AppGoogleMapsInput.vue";
import AppRadioButtonGroup from "@/shared/components/AppRadioButtonGroup.vue";
import {formatDateTime, formatDateMMDDHHmm, formatDateYYMMDD} from "@/shared/helpers/dates";
import AppTabsSwitch from "@/shared/components/AppTabsSwitch.vue";
import AppTable from "@/shared/components/AppTable.vue";
import AppButton from "@/shared/components/AppButton.vue";
import AppButtonIcon from "@/shared/components/AppButtonIcon.vue";
import AppTextInput from "@/shared/components/AppTextInput.vue";
import BottomPayment from "@/console-new/components/ops/counter/BottomPayment.vue";
import {useSunmiStore} from "@/console-sun/stores";
import CustomersPersonEditDetails from "@/console-new/components/ops/CustomersPersonEditDetails.vue";
import API from "@/shared/helpers/API";
import AppTableButtonGroup from "@/shared/components/AppTableButtonGroup.vue";
import BottomNumberEditor from "@/console-new/components/ops/counter/BottomNumberEditor.vue";

export default {
	setup() {
		const languageStore = useLanguageStore();
		const userStore = useUserStore();
		const notificationStore = useNotificationStore();
		const customersStore = useCustomersStore();
		const epsonStore = useEpsonStore();
		const ordersStore = useOrdersStore();
		const valuesStore = useValuesStore();
		const currencyStore = useCurrencyStore();
		const counterStore = useCounterStore();
		const svgStore = useSvgStore();
		const sunmiStore = useSunmiStore();
		return {getString: languageStore.getString, counterStore, svgStore, sunmiStore, showNotification: notificationStore.showNotification, currencyStore, userStore, customersStore, ordersStore, valuesStore, formatDateMMDDHHmm, formatDateYYMMDD, formatDateTime, epsonStore}
	},
	name: "CustomersPersonDetails",
	components: {
		BottomNumberEditor,
		AppTableButtonGroup,
		AppCard,
		BottomPayment, AppTextInput, AppButtonIcon, AppButton, AppTable, AppTabsSwitch, AppRadioButtonGroup, AppGoogleMapsInput, AppSelect, ModalHeader, AppBottomSheet, CustomersPersonEditDetails},
	mixins: [WindowWidth],
	emits: ['toggling-popup', 'deleted-customer'],
	props: {
		personId: {
			type: Number,
			default: null,
		},
	},
	data() {
		return {
			//history: 0, details: 1, topups: 2
			tabIndex: 0,
			topUpCredit: 0,
			paymentActive: false,
			activeAddCreditNumberEditor: false,
			animatingCredit: false,
			tempCreditValue: 0,
			cart: [],
			topupsFilters: [
				{
					type: "price",
					key: "amount",
					text: this.getString("amount"),
					active: false,
					value: null,
					activeItem: 0,
					finalValue: '',
				},
			],
			filters: [
				{
					type: "price",
					key: "total",
					text: this.getString("amount"),
					active: false,
					value: null,
					activeItem: 0,
					finalValue: '',
				},
				{
					type: "checklist",
					key: "readable_type",
					text: this.getString("type"),
					values: [
						this.getString('payment'),
						this.getString('order_status_x', [this.getString('not_paid')]),
						this.getString('order_status_x', [this.getString('paid')])
					],
					keysArray: ['payment', 'order_not_paid', 'order_paid'],
					active: false,
					selectedIndexes: [],
					finalValue: '',
				},
			],
			topupsTableHeadings: [
				{
					text: this.getString('date'),
					cols: 1,
					sortable: true,
					fieldName: 'date',
				},
				{
					text: this.getString('amount'),
					cols: 1,
					sortable: true,
					fieldName: 'readable_amount',
					sortFieldName: 'amount',
				},
				{
					text: this.getString('type'),
					cols: 1,
					fieldName: 'readable_type',
				},
			],
			tableHeadings: [
				{
					text: this.getString('id'),
					cols: 1,
					sortable: false,
					fieldName: 'id',
					slotName: 'order_number',
				},
				{
					text: this.getString('type'),
					cols: 1,
					fieldName: 'readable_type',
				},
				{
					text: this.getString('date'),
					cols: 1,
					sortable: true,
					fieldName: 'date',
				},
				{
					text: this.getString('amount'),
					cols: 1,
					sortable: true,
					fieldName: 'readable_total',
					sortFieldName: 'amount',
				},
				{
					text: this.getString('actions'),
					cols: 1,
					fieldName: 'settings',
					slotName: 'settingsIcon',
				},
			],
		}
	},
	computed: {
		customerCredit() {
			let refValue = this.animatingCredit ? this.tempCreditValue : this.person?.credit;
			return this.currencyStore.readableCurrencyValue(refValue, true, false)
		},
		mainCtaText() {
			return this.getString(this.tabIndex === 1 ? 'save' : 'add_credit');
		},
		person() {
			return this.personId ? this.customersStore.customersPeople.find(person => person?.customer_id === this.personId) : null;
		},
		bottomSheetWidth() {
			if (this.windowWidth < 550) {
				return "100%";
			} else if (this.windowWidth < 768) {
				return "550px";
			} else {
				return "720px";
			}
		},
		ordersToShow() {
			return this.customersStore.personOrders.map(order => {
				return {
					...order,
					readable_total: this.currencyStore.readableCurrencyValue(order.amount),
					readable_type:  this.getString(order.payment_id ? 'payment' : 'order_status_x', [order.type === 'paylater' ? this.getString('not_paid') : this.getString('paid')]),
					date: this.formatDateMMDDHHmm(order.date),
					id: order?.order_number_day ? order.order_number : order.payment_id
				}
			})
		},
		topupsToShow() {
			return this.customersStore.personCreditTopups.map(topup => {
				let readableType = '-';
				if(topup.type) {
					const typeKey = topup.type;
					readableType = this.getString(typeKey) || typeKey;
				}
				return {
					...topup,
					readable_amount: this.currencyStore.readableCurrencyValue(topup.amount),
					readable_type: readableType,
					date: this.formatDateMMDDHHmm(topup.date),
				}
			})
		},
		cartTotal() {
			return this.cart.reduce((acc, curr) => acc + curr.total, 0);
		},
		cartButtonDisabled() {
			if(!this.userStore.checkAnyOrderPayAllowed) {
				return true;
			}

			if(this.tabIndex === 0) {
				return this.cartTotal <= 0;
			} else {
				return true;
			}
		},
		payButtonText() {
			if(this.cartTotal > 0) {
				return this.getString('pay_x', [this.currencyStore.readableCurrencyValue(this.cartTotal)]);
			} else {
				return this.getString('pay');
			}
		},
	},
	methods: {
		animateCreditIncrease(amount) {
			if (!this.person) return;
			this.tempCreditValue = this.person.credit || 0;
			this.animatingCredit = true;
			const start = this.tempCreditValue || 0;
			const end = start + amount;
			const steps = 30; // Number of animation steps
			const duration = 1200; // Total duration in ms
			const stepValue = (end - start) / steps;
			let currentStep = 0;

			const interval = setInterval(() => {
				currentStep++;
				this.tempCreditValue = start + stepValue * currentStep;
				if (currentStep >= steps) {
					this.tempCreditValue = end;
					this.animatingCredit = false;
					if(this.person) {
						this.person.credit = end;
					}
					clearInterval(interval);
				}
			}, duration / steps);
		},
		buttonsPay(order) {
			return [
				{
					emitName: "picked-pay",
					title: this.getString(this.isOrderInCart(order) ? 'in_cart' : 'add'),
					activeFunction: (item) => this.isOrderInCart(item),
					disabled: !this.userStore.checkAnyOrderPayAllowed
				},
			]
		},
		switchedTab() {
			this.tabIndex = this.tabIndex === 1 ? 0 : 1;
		},
		goToOrder(orderId) {
			if(orderId) {
				this.ordersStore.search = "id:"+orderId;
				this.ordersStore.showCompleted = true;
				this.ordersStore.filterCounter = true;
				this.ordersStore.filterDelivery = true;
				this.ordersStore.filterTakeaway = true;
				this.ordersStore.filterRooms = true;
				this.ordersStore.refreshOrders();
				this.$router.push("/ops/orders");

				this.valuesStore.forceCloseAllBottomSheets = true;
				setTimeout(() => {
					this.valuesStore.forceCloseAllBottomSheets = false;
				}, 1000);
			}
		},
		fetchFunctionPersonOrdersPayments(filters, callback) {
			return this.customersStore.getFilteredPersonOrdersAndPayments(this.person.customer_id, filters, callback);
		},
		fetchFunctionPersonCreditTopups(filters, callback) {
			return this.customersStore.getFilteredPersonCreditTopups(this.person.customer_id, filters, callback);
		},
		addToCart(order) {
			let existing = this.cart.find(o => o.order_id === order.order_id);
			if(existing) {
				this.cart.splice(this.cart.indexOf(existing), 1);
			} else {
				this.cart.push({
					order_id: order.order_id,
					total: order.amount,
				});
			}
		},
		isOrderInCart(order) {
			return !!(this.cart.find(o => o.order_id === order.order_id));
		},
		tabButtonAction() {
			if(this.tabIndex === 1) {
				if(this.$refs['person-details']) {
					this.$refs['person-details'].updatePerson();
				}
			} else {
				this.activeAddCreditNumberEditor = true;
			}
		},
		togglingPopup(open) {
			this.$emit('toggling-popup', open);
			if(!open) {
				this.tabIndex = 0;
			}
		},
		togglingCreditNumberEditor(active) {
			if (!active) {
				this.activeAddCreditNumberEditor = false;
				this.counterStore.creditTopUpMode = false;
			}
		},
		confirmCreditTopUp(creditToAdd) {
			let amount = Number(creditToAdd || 0);
			if(amount === 0) {
				this.showNotification('no-changes');
				this.activeAddCreditNumberEditor = false;
				this.counterStore.creditTopUpMode = false;
			} else {
				this.topUpCredit = amount;
				// Set up credit top-up mode in store
				let person = this.customersStore.peoplesIds[this.person.customer_id];
				if(person) {
					this.counterStore.creditTopUpMode = true;
					this.counterStore.registryEditingCustomer = {...person, type: 'person'};
					this.paymentActive = true;
				} else {
					this.showNotification("generic-fail");
				}
			}
		},
		togglingPayment(open) {
			if(!open) {
				this.counterStore.creditTopUpMode = false;
				this.paymentActive = false;
			}
		},
		paymentDone(type, value, partialType, orderAssociatedId, itemsSplit, dutchPeople, discountOnTotal, discountPartial, appliedCoupons, printSingleItem, callback) {

			let isTopUp = this.counterStore.creditTopUpMode;
			const total = isTopUp ? this.topUpCredit : this.cartTotal;
			if(isTopUp) {
				this.paymentTopUpCredit(type, total, callback);
			} else {
				this.paymentDonePaymentsDue(type, total, discountOnTotal, callback);
			}

		},
		paymentDonePaymentsDue(type, total, discountOnTotal, callback) {
			API.init().payCustomersOrders(this.userStore.currBusinessId, this.cart.map(o => o.order_id), type, null, total, this.person.customer_id, "person", discountOnTotal, (response) => {
				if(!this.sunmiStore.cashmaticPendingDismissal) {
					this.paymentActive = false;
				}
				if(response && response.result && response.result === 'OK') {
					callback && callback(true);
					this.showNotification("payment-success");
					this.cart = [];
					this.epsonStore.customFiscalReceipt("Saldo cliente", total, type, discountOnTotal ? -discountOnTotal : 0);
					this.$emit('toggling-popup', false);
				} else {
					callback && callback(false);
					this.showNotification(response?.details === 'insufficient_credit' ? 'error-low-credit' : "generic-fail");
				}
			});
		},
		refreshCustomer(callback) {
			API.init().getCustomer(this.userStore.currBusinessId, this.person.customer_id, 'person', r => {
				let updatedPerson = r && r.result === "OK" ? r.customer : null;
				updatedPerson = updatedPerson ? this.customersStore.normalizeCustomerPerson(updatedPerson) : null;
				if(updatedPerson) {
					let currentIndex = this.customersStore.customersPeople.findIndex(p => p.customer_id === updatedPerson.customer_id);
					if(currentIndex !== -1) {
						this.customersStore.customersPeople.splice(currentIndex, 1, updatedPerson);
					}
					let regCustomer = this.counterStore?.registryEditingCustomer;
					let selCustomer = this.counterStore?.selectedCustomer;
					if(regCustomer?.type === 'person' && regCustomer?.customer_id === updatedPerson.customer_id) {
						this.counterStore.registryEditingCustomer = {...updatedPerson, type: 'person'};
					}
					if(selCustomer?.type === 'person' && selCustomer?.customer_id === updatedPerson.customer_id) {
						this.counterStore.selectedCustomer = {...updatedPerson, type: 'person'};
					}
				}
				if(!this.sunmiStore.cashmaticPendingDismissal) {
					this.paymentActive = false;
				}
				this.activeAddCreditNumberEditor = false;
				this.counterStore.creditTopUpMode = false;
				if(this.tabIndex === 2 && this.$refs['topups-table']) {
					this.$nextTick(() => {
						this.$refs['topups-table'].fetchData();
					});
				}
				callback && callback(r?.result === 'OK');
				this.showNotification(r?.result === 'OK' ? "payment-success" : "generic-fail");
			});
		},
		paymentTopUpCredit(type, total, callback) {
			// For Stripe/terminal payments, webhook will create the transaction
			// For non-Stripe payments, create the transaction now
			if(type === 'stripe') {
				this.animateCreditIncrease(total);
				this.refreshCustomer(callback);
			} else {
				API.init().topUpCustomerCredit(
					this.userStore.currBusinessId,
					total,
					this.person.customer_id,
					'person',
					type,
					(response) => {
						if(response && response.result === "OK") {
							this.animateCreditIncrease(total);
							this.refreshCustomer(callback);
						} else {
							if(!this.sunmiStore.cashmaticPendingDismissal) {
								this.paymentActive = false;
							}
							this.activeAddCreditNumberEditor = false;
							this.counterStore.creditTopUpMode = false;
							callback && callback();
							this.showNotification("generic-fail");
						}
					}
				);
			}
		},
		openBottomPayment() {
			if(this.cartTotal > 0) {
				let person = this.customersStore.peoplesIds[this.person.customer_id];
				if(person) {
					this.counterStore.registryEditingCustomer = {...person, type: 'person'};
					this.paymentActive = true;
				} else {
					this.showNotification("generic-fail");
					this.paymentActive = false;
				}
			}
		},
		emitDeletedPersonCustomer(personId) {
			this.$emit('deleted-customer', personId);
		},
		printCustomerCreditQr() {
			if(!this.person) {
				this.showNotification("generic-fail");
				return;
			}

			this.customersStore.printCustomerCreditQr(this.person.customer_id, 'person', (success, details) => {
				if(success) {
					this.showNotification("print-customer-credit-success");
				} else if(details === 'no_printers_found') {
					this.showNotification("no-printers-found");
				} else if(details === 'customer_not_found') {
					this.showNotification("warning-customer-missing");
				} else {
					this.showNotification("generic-fail");
				}
			});
		},
	},
	watch: {
		personId(newValue, oldValue) {
			if(newValue !== oldValue) {
				if(newValue) {
					this.tabIndex = 0;
					this.paymentActive = false;
					this.cart = [];
				}
			}
		},
	},
}
</script>

<style scoped lang="scss">
:deep(.table-wrapper) {
	padding-top: 0;
	margin-top: 10px;
	.row-actions {
		padding: 10px 10px 0;
	}
}
.orders-container {
	.card-container {
		box-shadow: none;
		&:deep(.card-header) {
			display: none;
		}
	}
}
.details-card {
	background-color: transparent;
	box-shadow: none;
	&:deep(.card-body) {
		padding: 10px 0;
	}
}
.table-text {
	font-size: 12px;
}

.payment-intent {
	text-align: left;
	width: 100%;
	font-size: 12px;

	&.clickable {
		cursor: pointer;
		color: var(--color-primary);
		text-decoration: underline;
	}
}
.sheet-heading {
	display: flex;
	flex-direction: column;
	gap: 10px;
	background-color: var(--color-bg-primary-2);
	border-radius: 13px;
	padding: 10px;
	margin-bottom: 20px;
	box-shadow: $box_shadow_inset;

	&-first-row {
		display: flex;
		justify-content: space-between;
		align-items: center;
		gap: 10px;
		width: 100%;

		@media (max-width: 600px) {
			flex-wrap: wrap;
		}
	}

	.first-row-ctas {
		gap: 10px;
	}

	.sheet-title {
		font-size: 18px;
		font-weight: bold;
		font-style: italic;
		width: calc(100% - 30px);
	}
	.print-icon-container {
		border: 1px solid var(--color-primary);
		padding: 10px;
		border-radius: 13px;
	}
	.edit-icon {
		margin-right: 10px;
		width: 18px;
		height: 18px;
	}
	.edit-icon, .print-icon {
		width: 20px;
		height: 20px;

		:deep(svg) {
			width: 100%;
			height: 100%;
			.fill {
				fill: var(--color-primary);
			}
			.stroke {
				stroke: var(--color-primary);
			}
		}
	}
	.edit-icon.active {
		:deep(svg) {
			.fill {
				fill: var(--color-secondary);
			}
			.stroke {
				stroke: var(--color-secondary);
			}
		}
	}

	.credit-display-container {
		gap: 10px;
	}
	.credit-display {
		background-color: var(--color-alpha-1);
		padding: 15px 10px;
		box-shadow: $box_shadow_inset;
		border-radius: 13px;
		max-width: calc(50% - 5px);
		cursor: pointer;

		@media (max-width: 600px) {
			padding: 15px 5px;
		}

		@media (max-width: 400px) {
			max-width: 100%;
		}

		&.debit {
			background-color: var(--color-display-alpha-red);
		}
		&.credit {
			transition: background-color 0.2s $transition .2s;
			background-color: var(--color-display-alpha-green);
			&.animating {
				background-color: rgba(var(--rgb-secondary), .4);
			}
		}

		p.balance-label {
			font-size: 14px;
		}
		p.balance-value {
			font-size: 24px;
		}
	}

}
</style>
